import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Sparkles, Loader2, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONFIDENCE_STYLE = {
  high: { bg: "#FEF2F2", border: "#FCA5A5", text: "#991B1B" },
  moderate: { bg: "#FFFBEB", border: "#FDE68A", text: "#92400E" },
  low: { bg: "#F0FDF4", border: "#86EFAC", text: "#166534" },
};

export default function InsightsCard() {
  const { activeId } = useProfile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const fetchInsight = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/insights/journal");
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load insights.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeId) return;
    fetchInsight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  return (
    <div
      className="card-warm p-6 fade-up"
      style={{
        background: "linear-gradient(160deg, #FDF0EA 0%, #F4EFE6 100%)",
        borderColor: "#E5DEC9",
      }}
      data-testid="insights-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: "#D96B43" }}>
            <Sparkles size={16} color="white" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "#BF5832" }}>
              Pattern insight
            </p>
            <h3 className="font-serif-display text-xl font-medium" style={{ color: "#1C2826" }}>
              What your body is saying
            </h3>
          </div>
        </div>
        <Button
          onClick={fetchInsight}
          disabled={loading}
          variant="ghost"
          size="sm"
          className="rounded-full"
          data-testid="insights-refresh-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
        </Button>
      </div>

      {loading && !data ? (
        <p className="text-sm" style={{ color: "#5C6B66" }}>
          Looking for patterns across your last entries…
        </p>
      ) : err ? (
        <p className="text-sm" style={{ color: "#991B1B" }}>{err}</p>
      ) : data ? (
        <>
          <p className="text-base leading-relaxed" style={{ color: "#1C2826" }} data-testid="insights-headline">
            {data.headline}
          </p>
          {data.patterns && data.patterns.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="insights-patterns">
              {data.patterns.map((p, i) => {
                const style = CONFIDENCE_STYLE[p.confidence] || CONFIDENCE_STYLE.moderate;
                return (
                  <div
                    key={i}
                    className="p-3 rounded-xl border"
                    style={{ background: "#FFFFFF", borderColor: "#E5DEC9" }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border"
                        style={{ background: style.bg, borderColor: style.border, color: style.text }}
                      >
                        {p.confidence} confidence
                      </span>
                      <span className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                        {p.title}
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "#5C6B66" }}>{p.evidence}</p>
                    {p.recommendation && (
                      <p className="text-xs mt-1.5" style={{ color: "#23473C" }}>
                        <strong>Suggestion:</strong> {p.recommendation}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {data.encouragement && (
            <p className="mt-3 text-xs italic" style={{ color: "#5C3B2A" }}>
              {data.encouragement}
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
